/**
 * „Czego dotyczyło” w dzienniku działań — nazwa dla człowieka zamiast kodu.
 *
 * API zapisuje w `AdminActionLog.subject` kod zasobu (`company`,
 * `suggestion-parameter`…) i oddaje go bez zmian; kody to dane (są w bazie
 * i w filtrze `subject`), więc tłumaczymy je dopiero tu, przy wyświetlaniu.
 * Weryfikacja na dev (28.09.2026): kolumna pokazywała surowe kody.
 *
 * Źródła kodów: wywołania `auditLog.record({ subject: … })` w API
 * (users, companies, contractors, carriers, via, suggestion-parameters,
 * feature-flags, usage-events).
 * Kod spoza mapy pokazujemy wprost — nie znika i nie jest zgadywany.
 */
export const AUDIT_SUBJECT_LABELS: Record<string, string> = {
  user: 'Użytkownik',
  company: 'Spółka',
  contractor: 'Kontrahent',
  carrier: 'Przewoźnik',
  'carrier-contact': 'Kontakt przewoźnika',
  'via-order': 'Zlecenie VIA',
  'order.szczegoly-zapisu': 'Szczegóły zapisu do VIA',
  'suggestion-parameter': 'Parametr podpowiedzi',
  'feature-flag': 'Przełącznik funkcji',
  // Ręczne „Usuń dane starsze niż…” na ekranie „Pomiar użycia” (23.10.2026).
  'usage-events': 'Pomiar użycia',
};

export const SUGGESTION_PARAMETER_SUBJECT = 'suggestion-parameter';

/** Nazwa przedmiotu; nieznany kod wprost, brak — kreska. */
export function auditSubjectLabel(code: unknown): string {
  if (typeof code !== 'string' || code === '') return '—';
  return AUDIT_SUBJECT_LABELS[code] ?? code;
}

/**
 * Klucz parametru w opisie wpisu: „<nazwa> (<klucz>): …” — sam identyfikator
 * w nawiasie tuż przed pierwszym dwukropkiem.
 */
const PARAMETER_KEY_IN_DETAILS = /^([^:]*?) \(([A-Za-z][A-Za-z0-9_]*)\):/;

/**
 * Klucz parametru podpowiedzi z opisu wpisu.
 *
 * Parametr ma klucz tekstowy, więc `subjectId` jest `null`. Do 07.10.2026
 * API pisało opis jako „<nazwa> (<klucz>): <stara> → <nowa>; powód: …”,
 * od 07.10 bez klucza — stare wpisy w bazie mają go nadal. Inny kształt
 * opisu = brak klucza.
 */
export function suggestionParameterKey(details: unknown): string | null {
  if (typeof details !== 'string') return null;
  const m = PARAMETER_KEY_IN_DETAILS.exec(details);
  return m ? m[2] : null;
}

/**
 * Pierwsza część komórki: nazwa przedmiotu z numerem.
 *
 * Parametr podpowiedzi bez klucza technicznego (07.10.2026, lista
 * komunikatów z 01.10: „dziennik działań dopisuje go do opisu”) — który
 * parametr, mówi nazwa na początku opisu.
 */
export function auditSubjectText(record: Readonly<Record<string, unknown>>): string {
  const label = auditSubjectLabel(record.subject);
  if (record.subjectId != null) return `${label} nr ${String(record.subjectId)}`;
  return label;
}

/**
 * Opis wpisu do pokazania. Wpisy parametrów sprzed 07.10.2026 mają po
 * nazwie klucz techniczny w nawiasie — zdejmujemy go przy wyświetlaniu
 * (wiersz w bazie zostaje bez zmian). Inne opisy idą wprost.
 */
export function auditDetailsText(record: Readonly<Record<string, unknown>>): string | null {
  if (record.details == null) return null;
  const details = String(record.details);
  if (record.subject !== SUGGESTION_PARAMETER_SUBJECT) return details;
  return details.replace(PARAMETER_KEY_IN_DETAILS, '$1:');
}
